import { apiService } from '../../../services/apiService';
import {
  API_PATHS,
  getDeleteRoutingRulePath,
  getToggleRoutingRulePath,
  getUpdateRoutingRulePath,
} from '../../../utils/apiPaths';
import type {
  RiskField,
  RoutingMid,
  RoutingOverview,
  RoutingRule,
  RoutingRulePayload,
  RoutingSimulation,
} from '../models/types';

/** Admin callers pass a merchantId; merchant sessions omit it and are scoped server-side. */
function withMerchant(path: string, merchantId?: number | null): string {
  return merchantId ? `${path}?merchantId=${merchantId}` : path;
}

function unwrap<T>(res: any, fallback: T): T {
  if (!res?.success) {
    throw new Error(res?.message || 'Request failed.');
  }
  return (res.data ?? fallback) as T;
}

export async function fetchRoutingOverview(merchantId?: number | null): Promise<RoutingOverview> {
  const res: any = await apiService.get(withMerchant(API_PATHS.GET_ROUTING_OVERVIEW, merchantId));
  return unwrap<RoutingOverview>(res, {
    merchantAccounts: 0,
    activeMerchantAccounts: 0,
    routingRules: 0,
    activeRoutingRules: 0,
    countriesCovered: 0,
    coveragePercent: 0,
  });
}

export async function fetchRoutingRules(merchantId?: number | null): Promise<RoutingRule[]> {
  const res: any = await apiService.get(withMerchant(API_PATHS.GET_ROUTING_RULES, merchantId));
  return unwrap<RoutingRule[]>(res, []);
}

export async function fetchRoutingMids(merchantId?: number | null): Promise<RoutingMid[]> {
  const res: any = await apiService.get(withMerchant(API_PATHS.GET_ROUTING_MIDS, merchantId));
  return unwrap<RoutingMid[]>(res, []);
}

/** The catalog of risk-rule fields a routing group can condition on (RiskRuleType value + label). */
export async function fetchRiskFields(): Promise<RiskField[]> {
  const res: any = await apiService.get(API_PATHS.GET_ROUTING_RISK_FIELDS);
  return unwrap<RiskField[]>(res, []);
}

/** ALL of the (scoped) merchant's terminals (id + name) — for the rule dialog's terminal-scope
 *  selector, independent of whether a terminal has an acquirer mapping. */
export async function fetchRoutingMerchantTerminals(
  merchantId?: number | null,
): Promise<{ id: number; name: string }[]> {
  const res: any = await apiService.get(withMerchant(API_PATHS.GET_ROUTING_MERCHANT_TERMINALS, merchantId));
  return unwrap<{ id: number; name: string }[]>(res, []);
}

export async function createRoutingRule(payload: RoutingRulePayload): Promise<RoutingRule> {
  const res: any = await apiService.post(API_PATHS.CREATE_ROUTING_RULE, payload);
  return unwrap<RoutingRule>(res, {} as RoutingRule);
}

export async function updateRoutingRule(
  ruleId: number,
  payload: RoutingRulePayload,
): Promise<RoutingRule> {
  const res: any = await apiService.put(getUpdateRoutingRulePath(ruleId), payload);
  return unwrap<RoutingRule>(res, {} as RoutingRule);
}

/** The single GLOBAL "no fallback" denial message, shared by every rule. */
export async function fetchRoutingFallbackMessage(): Promise<string> {
  const res: any = await apiService.get(API_PATHS.ROUTING_FALLBACK_MESSAGE);
  return unwrap<{ message?: string }>(res, {}).message ?? '';
}

export async function setRoutingFallbackMessage(message: string): Promise<string> {
  const res: any = await apiService.patch(API_PATHS.ROUTING_FALLBACK_MESSAGE, { message });
  return unwrap<{ message?: string }>(res, {}).message ?? '';
}

export async function setRoutingRuleActive(ruleId: number, active: boolean): Promise<void> {
  const res: any = await apiService.patch(getToggleRoutingRulePath(ruleId, active), {});
  unwrap(res, null);
}

export async function deleteRoutingRule(ruleId: number): Promise<void> {
  const res: any = await apiService.post(getDeleteRoutingRulePath(ruleId), {});
  unwrap(res, null);
}

export async function reorderRoutingRules(
  ruleIds: number[],
  merchantId?: number | null,
): Promise<void> {
  const res: any = await apiService.post(API_PATHS.REORDER_ROUTING_RULES, { merchantId, ruleIds });
  unwrap(res, null);
}

export async function simulateRouting(
  input: {
    countryCode: string;
    currency: string;
    amount: number;
    deviceType?: string;
    firedRiskRules?: string[];
  },
  merchantId?: number | null,
): Promise<RoutingSimulation> {
  const res: any = await apiService.post(API_PATHS.SIMULATE_ROUTING, { ...input, merchantId });
  return unwrap<RoutingSimulation>(res, {} as RoutingSimulation);
}

export async function fetchActiveMerchants(): Promise<{ id: number; name: string }[]> {
  const res: any = await apiService.post(API_PATHS.GET_MERCHANTS, {
    page: 0,
    size: 200,
    status: 'ACTIVE',
  });
  if (!res?.success) return [];
  const pageData = res?.data?.content ? res.data : res;
  const rows = pageData.content || [];
  return rows
    .map((m: Record<string, unknown>) => ({
      id: Number(m.id),
      name: String(m.name || `Merchant ${m.id}`),
    }))
    .filter((m: { id: number }) => m.id > 0);
}
